import { SegmentedControl, TextField } from '@genesislcap/foundation-zero';
import {
  attr,
  customElement,
  FASTElement,
  observable,
  Notifier,
  Subscriber,
  Observable,
  Splice,
} from '@microsoft/fast-element';
import {
  ICriteriaObject,
  MarketDepthCriteriaType,
  MarketDepthField,
  MarketDepthOperation,
  MarketDepthTable,
} from '../../../../routes/market-depth/market-depth';
import { ICommonService } from '../../../../services';
import { EnumTranslate, CASCADE_ENUM, IEnumService } from '../../../../services/enum.service';
import { IHubZoneMatrix } from '../../../../types';
import { IMarketDepthService } from '../../../../services/market-depth.service';
import {
  clearErrorUtil,
  CONTRACT_TYPE,
  displayErrorUtil,
  METHOD_EXECUTION,
  PROJECT_OPERATIONAL_STATUS,
  PRODUCT_TYPE,
  TECHNOLOGY,
} from '../../../../utils';
import { formatCriteriaDateMinutesPrecisionFromYear } from '../../../../utils/formatting';
import { MarketDepthQueryStyles as styles } from './market-depth-query.style';
import { MarketDepthQueryTemplate as template } from './market-depth-query.template';
import { IMultiSelectOption, MultiSelect } from '../../../common/multi-select';
import { ICompanyService } from '../../../../services/company.service';

const name = 'market-depth-query';

@customElement({
  name,
  template,
  styles,
})
export class MarketDepthQuery extends FASTElement {
  @ICommonService commonService: ICommonService;
  @ICompanyService companyService: ICompanyService;
  @IEnumService enumService: IEnumService;
  @IMarketDepthService marketDepthService: IMarketDepthService;

  @attr criteria: Array<ICriteriaObject> = [];
  @attr enums: Array<EnumTranslate>;
  @attr contractTypes: Array<EnumTranslate>;

  @observable productTypeValue: string = this.chooseProductTypeDefault();
  @observable _iso = [];
  @observable _applicableRecProgramList: Array<IMultiSelectOption> = [];
  @observable _settlementPoint: Array<string> = [];
  @observable _settlementPointList: Array<IMultiSelectOption> = [];
  @observable _settlementIndex: Array<string> = [];
  @observable priceMinValue: number;
  @observable priceMaxValue: number;
  @observable tenorDateMinValue: number;
  @observable tenorDateMaxValue: number;
  @observable contractTypeValue: string;
  @observable techTypeValue: TECHNOLOGY = TECHNOLOGY.BOTH;
  @observable methodOfExecutionValue: METHOD_EXECUTION = METHOD_EXECUTION.RFQ;
  @observable applicableRECProgramTypes: Array<EnumTranslate>;

  @observable technologyTypeElement: SegmentedControl;
  @observable methodOfExecutionElement: SegmentedControl;
  @observable contractTypeElement: SegmentedControl;
  @observable isoElement: MultiSelect;
  @observable priceMinElement: TextField;
  @observable priceMaxElement: TextField;
  @observable tenorDateMinElement: TextField;
  @observable tenorDateMaxElement: TextField;
  @observable recElement: MultiSelect;
  @observable settlementPointsElement: MultiSelect;

  @observable settlementPointDisabled: boolean = true;

  @observable selectedSettlementPoints = [];
  @observable selectedRecPrograms = [];
  @observable newBuildChecked: boolean = false;
  @observable postBuildChecked: boolean = false;

  private _hubZoneMatrix: Array<IHubZoneMatrix> = [];

  private currentYear = new Date().getFullYear();
  @observable minYear = this.currentYear - 20;
  @observable maxYear = this.currentYear + 20;

  private isosSelected: Array<String> = [];
  isoNotifier: Notifier;
  settlementPointNotifier: Notifier;
  recProgramNotifier: Notifier;
  isoHandler: Subscriber;
  settlementPointHandler: Subscriber;
  recProgramHandler: Subscriber;

  connectedCallback() {
    super.connectedCallback();
    this.createEventListeners();
    this.getAllIso().then(
      async () =>
        await this.loadRecProgramEnums().then(() => {
          this.criteria = this.marketDepthService.criteriaList;
          if (this.criteria.length > 0) {
            this.loadCriteria();
          } else {
            // No existing criteria
            this.generateISODD().then(() =>
              this.generateSettlementPointDD().then(() => (this.settlementPointDisabled = false)),
            );
            this.generateApplicableRecProgramDD();
            this.techTypeValue = TECHNOLOGY.BOTH;
            this.contractTypeValue = null;
            this.productTypeValue = this.chooseProductTypeDefault();
            this.handleContractTypeValue();
          }
        }),
    );
  }

  private multiSelectListener() {
    const _this = this;
    this.isoNotifier = Observable.getNotifier(this.isoElement);
    this.settlementPointNotifier = Observable.getNotifier(this.settlementPointsElement);
    this.recProgramNotifier = Observable.getNotifier(this.recElement);
    this.isoHandler = {
      handleChange(source: any, splices: Splice[]) {
        _this.isosSelected = _this.isoElement.getSelectedIds();
        _this.isoSelected().then(() => _this.updateCriteria());
      },
    };

    this.settlementPointHandler = {
      handleChange(source: any, splices: Splice[]) {
        _this.selectedSettlementPoints = _this.settlementPointsElement.getSelectedIds();
        _this.updateCriteria();
      },
    };

    this.recProgramHandler = {
      handleChange(source: any, splices: Splice[]) {
        _this.selectedRecPrograms = _this.recElement.getSelectedIds();
        _this.updateCriteria();
      },
    };

    this.isoNotifier.subscribe(this.isoHandler, 'selectedIds');
    this.settlementPointNotifier.subscribe(this.settlementPointHandler, 'selectedIds');
    this.recProgramNotifier.subscribe(this.recProgramHandler, 'selectedIds');
  }

  private loadCriteria() {
    const technologiesSelected = this.criteria.filter(
      (elm) => elm.FIELD === MarketDepthField.TECHNOLOGY,
    );
    if (technologiesSelected.length === 1) {
      this.techTypeValue = technologiesSelected[0].VALUE as TECHNOLOGY;
    } else {
      // If no technology field in criteria, default to both
      this.techTypeValue = TECHNOLOGY.BOTH;
    }
    // Contract type of null means both (New and Post build)
    this.contractTypeValue =
      (this.criteria.find(
        (elm) =>
          elm.FIELD === MarketDepthField.CONTRACT_TYPE &&
          elm.OPERATION === MarketDepthOperation.EQUAL,
      )?.VALUE as CONTRACT_TYPE) ?? null;
    this.productTypeValue = this.criteria.find((elm) => elm.FIELD === MarketDepthField.PRODUCT_TYPE)
      ?.VALUE as string;
    this.methodOfExecutionValue =
      (this.criteria.find((elm) => elm.FIELD === MarketDepthField.METHOD_EXECUTION)
        ?.VALUE as METHOD_EXECUTION) ?? METHOD_EXECUTION.RFQ;
    this.handleContractTypeValue();
    // Need to save the following values before making changes or the data will be overridden:
    // existing iso, settlement point, rec program, price and tenor date filters
    const existingIsoCriteria = this.criteria.filter(
      (criteria) => criteria.FIELD === MarketDepthField.ISO,
    );
    const existingSettlementCriteria = this.criteria.filter(
      (criteria) => criteria.FIELD === MarketDepthField.SETTLEMENT_POINT,
    );
    const existingRecCriteria = this.criteria.filter(
      (criteria) => criteria.FIELD === MarketDepthField.APPLICABLE_REC_PROGRAM,
    );

    const existingPriceMinCriteria =
      (this.criteria.find(
        (criteria) => criteria.OPERATION === MarketDepthOperation.GREATER_OR_EQUAL,
      )?.VALUE as number) ?? null;
    const existingPriceMaxCriteria =
      (this.criteria.find((criteria) => criteria.OPERATION === MarketDepthOperation.LOWER_OR_EQUAL)
        ?.VALUE as number) ?? null;
    const existingTenorDateMinCriteria =
      (this.criteria.find((criteria) => criteria.OPERATION === MarketDepthOperation.BIGGER)
        ?.VALUE as number) ?? null;
    const existingTenorDateMaxCriteria =
      (this.criteria.find((criteria) => criteria.OPERATION === MarketDepthOperation.SMALLER)
        ?.VALUE as number) ?? null;

    // Now we can load the data from session storage
    if (existingIsoCriteria.length > 0) {
      this.isosSelected = existingIsoCriteria.map((criteria) => criteria.VALUE as string);
      // Set all items to null
      this.resetISOChecklist(); // make all the items set to null
      this._iso.forEach((element) => {
        element.CHECKED = this.isosSelected.includes(element.ITEM_ID);
      });
      this.isoElement.setDataList(this._iso);
    }

    if (existingRecCriteria.length > 0) {
      this.selectedRecPrograms = existingRecCriteria.map((criteria) => criteria.VALUE as string);
      // Set all items to null
      this.resetRecChecklist();
      this._applicableRecProgramList.forEach((element) => {
        element.CHECKED = this.selectedRecPrograms.includes(element.ITEM_ID);
      });
      this.recElement.setDataList(this._applicableRecProgramList);
    } else {
      this.generateApplicableRecProgramDD();}

    this.priceMinValue = existingPriceMinCriteria;
    this.priceMaxValue = existingPriceMaxCriteria;
    this.priceMinElement.value = this.priceMinValue?.toString() ?? '';
    this.priceMaxElement.value = this.priceMaxValue?.toString() ?? '';

    this.tenorDateMinValue = existingTenorDateMinCriteria;
    this.tenorDateMaxValue = existingTenorDateMaxCriteria;
    // Format the Date so that it's just the year and not the full date (ex 20250101 -> 2025)
    this.tenorDateMinValue = this.tenorDateMinValue
      ? Math.floor(this.tenorDateMinValue / 10000)
      : null;
    this.tenorDateMaxValue = this.tenorDateMaxValue
      ? Math.floor(this.tenorDateMaxValue / 10000)
      : null;
    this.tenorDateMinElement.value = this.tenorDateMinValue?.toString() ?? '';
    this.tenorDateMaxElement.value = this.tenorDateMaxValue?.toString() ?? '';

    // When we set the iso data, that clears the settlement point data. And now we want to reset the settlement point data
    if (existingSettlementCriteria.length > 0) {
      this.selectedSettlementPoints = existingSettlementCriteria.map(
          (criteria) => criteria.VALUE as string,
      );
      this.resetSettlementPointChecklist();
      this._settlementPointList.forEach((element) => {
        element.CHECKED = this.selectedSettlementPoints.includes(element.ITEM_ID);
      });
      this.settlementPointsElement.setDataList(this._settlementPointList);
    }
  }

  private chooseProductTypeDefault() {
    return this.companyService.isSefParticipantAgreement()
      ? PRODUCT_TYPE.VIRTUAL_PPA
      : PRODUCT_TYPE.PHYSICAL_PPA;
  }

  private createEventListeners() {
    this.multiSelectListener();
    this.priceMinElement.addEventListener('blur', () => this.checkPriceRules());
    this.priceMaxElement.addEventListener('blur', () => this.checkPriceRules());
    this.tenorDateMinElement.addEventListener('blur', () => this.checkTenorDateRules());
    this.tenorDateMaxElement.addEventListener('blur', () => this.checkTenorDateRules());
    this.tenorDateMinElement.addEventListener('change', () => this.checkMinTenorDate());
    this.tenorDateMaxElement.addEventListener('change', () => this.checkMinTenorDate());
  }

  public technologyChanged(technology: TECHNOLOGY) {
    this.techTypeValue = technology;
    this.updateCriteria();
  }

  public productTypeChanged() {
    this.updateCriteria();
  }

  public updateCriteria() {
    /*
    Each criteria item is either set to:
      1. the value(s) selected (if the value is set) or
      2. the default value for showing all RQS and contracts (if the value is empty)
    */
    const criteriaList: Array<ICriteriaObject> = [];

    if (this.isosSelected.length > 0) {
      this.isosSelected.forEach((iso) =>
        criteriaList.push({
          FIELD: MarketDepthField.ISO,
          VALUE: iso,
          OPERATION: MarketDepthOperation.EQUAL,
          TABLE: MarketDepthTable.BOTH,
          TYPE: MarketDepthCriteriaType.STRING,
        }),
      );
    } else {
      criteriaList.push({
        FIELD: MarketDepthField.ISO,
        VALUE: 'NONE_VALID',
        OPERATION: MarketDepthOperation.EQUAL,
        TABLE: MarketDepthTable.BOTH,
        TYPE: MarketDepthCriteriaType.STRING,
      });
    }

    if (
      this.selectedRecPrograms.length > 0 &&
      this.methodOfExecutionValue !== METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED
    ) {
      this.selectedRecPrograms.forEach((rec) =>
        criteriaList.push({
          FIELD: MarketDepthField.APPLICABLE_REC_PROGRAM,
          VALUE: rec,
          OPERATION: MarketDepthOperation.EQUAL,
          TABLE: MarketDepthTable.BOTH,
          TYPE: MarketDepthCriteriaType.STRING,
        }),
      );
      criteriaList.push({
        FIELD: MarketDepthField.APPLICABLE_REC_PROGRAM,
        VALUE: 'ANY',
        OPERATION: MarketDepthOperation.EQUAL,
        TABLE: MarketDepthTable.BOTH,
        TYPE: MarketDepthCriteriaType.STRING,
      });
    }

    if (this.techTypeValue != TECHNOLOGY.BOTH) {
      criteriaList.push({
        FIELD: MarketDepthField.TECHNOLOGY,
        VALUE: this.techTypeValue,
        OPERATION: MarketDepthOperation.EQUAL,
        TABLE: MarketDepthTable.BOTH,
        TYPE: MarketDepthCriteriaType.STRING,
      });
    }

    if (this.productTypeValue) {
      criteriaList.push({
        FIELD: MarketDepthField.PRODUCT_TYPE,
        VALUE: this.productTypeValue,
        OPERATION: MarketDepthOperation.EQUAL,
        TABLE: MarketDepthTable.BOTH,
        TYPE: MarketDepthCriteriaType.STRING,
      });
    }

    if (this.methodOfExecutionValue) {
      criteriaList.push({
        FIELD: MarketDepthField.METHOD_EXECUTION,
        VALUE: this.methodOfExecutionValue,
        OPERATION: MarketDepthOperation.EQUAL,
        TABLE: MarketDepthTable.BOTH,
        TYPE: MarketDepthCriteriaType.STRING,
      });
      if (this.methodOfExecutionValue === METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
        criteriaList.push({
          FIELD: MarketDepthField.CONTRACT_TYPE,
          VALUE: METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
          OPERATION: MarketDepthOperation.EQUAL,
          TABLE: MarketDepthTable.BOTH,
          TYPE: MarketDepthCriteriaType.STRING,
        });
      } else if (this.methodOfExecutionValue === METHOD_EXECUTION.RFQ) {
        criteriaList.push({
          FIELD: MarketDepthField.CONTRACT_TYPE,
          VALUE: METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
          OPERATION: MarketDepthOperation.NOT_EQUAL,
          TABLE: MarketDepthTable.BOTH,
          TYPE: MarketDepthCriteriaType.STRING,
        });
        if (this.contractTypeValue) {
          criteriaList.push({
            FIELD: MarketDepthField.CONTRACT_TYPE,
            VALUE: this.getContractTypeByProductType(),
            OPERATION: MarketDepthOperation.EQUAL,
            TABLE: MarketDepthTable.BOTH,
            TYPE: MarketDepthCriteriaType.STRING,
          });
        }
        if (this.productTypeValue == PRODUCT_TYPE.RECS) {
          criteriaList.push({
            FIELD: MarketDepthField.PROJECT_OPERATIONAL_STATUS,
            VALUE: this.getProjectOperationalStatus(),
            OPERATION: MarketDepthOperation.EQUAL,
            TABLE: MarketDepthTable.VPPA,
            TYPE: MarketDepthCriteriaType.STRING,
          });
        }
        criteriaList.push({
          FIELD: MarketDepthField.STATUS,
          VALUE: 'AWAITING_KEY_METRICS',
          OPERATION: MarketDepthOperation.NOT_EQUAL,
          TABLE: MarketDepthTable.BOTH,
          TYPE: MarketDepthCriteriaType.STRING,
        });
      }
    }

    if (this.priceMinValue) {
      criteriaList.push({
        FIELD: MarketDepthField.PRICE_MIN,
        VALUE: this.priceMinValue,
        OPERATION: MarketDepthOperation.GREATER_OR_EQUAL,
        TABLE: MarketDepthTable.BOTH,
        TYPE: MarketDepthCriteriaType.NUMBER,
      });
    }

    if (this.priceMaxValue) {
      criteriaList.push({
        FIELD: MarketDepthField.PRICE_MAX,
        VALUE: this.priceMaxValue,
        OPERATION: MarketDepthOperation.LOWER_OR_EQUAL,
        TABLE: MarketDepthTable.BOTH,
        TYPE: MarketDepthCriteriaType.NUMBER,
      });
    }

    if (this.selectedSettlementPoints) {
      this.selectedSettlementPoints.forEach((elm) => {
        criteriaList.push({
          FIELD: MarketDepthField.SETTLEMENT_POINT,
          VALUE: elm,
          OPERATION: MarketDepthOperation.EQUAL,
          TABLE: MarketDepthTable.BOTH,
          TYPE: MarketDepthCriteriaType.STRING,
        });
      });
    }

    if (this.tenorDateMinValue) {
      const formatedDate = formatCriteriaDateMinutesPrecisionFromYear(this.tenorDateMinValue, true);
      criteriaList.push({
        FIELD: MarketDepthField.TENOR_START_DATE,
        VALUE: formatedDate,
        OPERATION: MarketDepthOperation.BIGGER,
        TABLE: MarketDepthTable.BOTH,
        TYPE: MarketDepthCriteriaType.DATE,
      });
    }

    if (this.tenorDateMaxValue) {
      const formatedDate = formatCriteriaDateMinutesPrecisionFromYear(
        this.tenorDateMaxValue,
        false,
      );
      criteriaList.push({
        FIELD: MarketDepthField.TENOR_START_DATE,
        VALUE: formatedDate,
        OPERATION: MarketDepthOperation.SMALLER,
        TABLE: MarketDepthTable.BOTH,
        TYPE: MarketDepthCriteriaType.DATE,
      });
    }

    this.criteria = criteriaList;
    this.marketDepthService.criteriaList = criteriaList;
  }

  public resetCriteria() {
    sessionStorage.removeItem('criteria');
    this.generateISODD();
    this.generateApplicableRecProgramDD();
    this.selectedRecPrograms = [];
    this.selectedSettlementPoints = [];
    this.generateSettlementPointDD();
    this.techTypeValue = TECHNOLOGY.BOTH;
    this.productTypeValue = this.chooseProductTypeDefault();
    this.contractTypeValue = null;
    this.priceMinValue = null;
    this.priceMaxValue = null;
    this.tenorDateMinValue = null;
    this.tenorDateMaxValue = null;
    this.methodOfExecutionValue = METHOD_EXECUTION.RFQ;
    this.postBuildChecked = false;
    this.newBuildChecked = false;
    this.settlementPointDisabled = false;
    this.validateFields();
    this.updateCriteria();
  }

  private validateFields(): boolean {
    if (!this.checkPriceRules() || !this.checkTenorDateRules()) {
      return false;
    }

    return true;
  }

  private checkPriceRules(): boolean {
    let isValid: boolean = true;
    if (this.priceMinValue && this.priceMaxValue && +this.priceMinValue >= +this.priceMaxValue) {
      displayErrorUtil(this.priceMinElement, 'Max Price must be greater than Min Price');
      isValid = false;
    } else {
      clearErrorUtil(this.priceMinElement);
    }
    return isValid;
  }

  private checkTenorDateRules(): boolean {
    let isValid: boolean = true;

    if (
      this.tenorDateMinValue &&
      this.tenorDateMaxValue &&
      +this.tenorDateMinValue > +this.tenorDateMaxValue
    ) {
      displayErrorUtil(
        this.tenorDateMinElement,
        'End Year must be greater than or equal to Start Year',
      );
      isValid = false;
    } else {
      clearErrorUtil(this.tenorDateMinElement);
    }
    return isValid;
  }

  private checkMinTenorDate() {
    if (
      (this.tenorDateMinValue && this.tenorDateMinValue < this.minYear) ||
      this.tenorDateMinValue > this.maxYear
    ) {
      this.tenorDateMinValue = this.currentYear;
    }

    if (
      (this.tenorDateMaxValue && this.tenorDateMaxValue < this.minYear) ||
      this.tenorDateMaxValue > this.maxYear
    ) {
      if (this.tenorDateMinValue) {
        this.tenorDateMaxValue = this.tenorDateMinValue;
      } else {
        this.tenorDateMaxValue = this.currentYear;
      }
    }
  }

  // Used to set the Hub Zone Matrix
  private async getAllIso(): Promise<void> {
    const response = await this.commonService.getHubZoneMatrix();
    if (response.REPLY) {
      this._hubZoneMatrix = response.REPLY;
    }
  }

  private generateApplicableRecProgramDD() {
      this._applicableRecProgramList = [
        ...new Set(
          this.applicableRECProgramTypes.map((item) => {
            const value: IMultiSelectOption = {
              CHECKED: false,
              ITEM_ID: item.ENUM_VALUE,
              ITEM_DESC: item.READABLE_VALUE,
              DISABLED: false,
              CATEGORY: '',
            };
            return value;
          }),
        ),
      ];
      this.recElement.setDataList(this._applicableRecProgramList);
  }

  private async generateSettlementPointDD(): Promise<void> {
    const selectedPoints = this._hubZoneMatrix.filter((item) =>
        this.isosSelected.includes(item.ISO),
    );

    // Get Unique Settlement Points for DD
    this._settlementPoint = [...new Set(selectedPoints.map((item) => item.SETTLEMENT_POINT))];

    this._settlementPointList = [
      ...new Set(
          this._settlementPoint.map((item) => {
            const value: IMultiSelectOption = {
              CHECKED: false,
              ITEM_ID: item,
              ITEM_DESC: item,
              DISABLED: false,
              CATEGORY: '',
            };
            return value;
          }),
      ),
    ];

    this.settlementPointsElement.setDataList(this._settlementPointList);
  }

  // Set all ISOs in the Hub Zone Matrix to be selected
  private async generateISODD(): Promise<void> {
    const isoSet = new Set<string>();
    this._iso = [];

    this._hubZoneMatrix.forEach((item) => {
      if (!isoSet.has(item.ISO)) {
        const value: IMultiSelectOption = {
          CHECKED: true,
          ITEM_ID: item.ISO,
          ITEM_DESC: item.ISO,
          DISABLED: false,
          CATEGORY: '',
        };
        this._iso.push(value);
        this.isosSelected.push(item.ISO);
        isoSet.add(item.ISO);
      }
    });
    this.isoElement.setDataList(this._iso);
  }

  public async isoSelected(): Promise<void>{
    // Reset previously selected values
    this._settlementIndex = [];
    this._settlementPoint = [];
    this._settlementPointList = [];

    // Generate dropdown values
    if (this.isosSelected.length > 0) {
      await this.generateSettlementPointDD();
      if (this.selectedSettlementPoints) {
        this._settlementPointList.forEach((element) => {
          element.CHECKED = this.selectedSettlementPoints.includes(element.ITEM_ID);
        });
        this.settlementPointsElement.setDataList(this._settlementPointList);
      }
      this.settlementPointDisabled = false;
    } else {
      // No ISOs selected so disable settlement points dropdown
      this._settlementPoint = [];
      this._settlementPointList = [];

      this.settlementPointsElement.setDataList(this._settlementPointList);
      this.settlementPointDisabled = true;
    }
  }

  public methodOfExecutionChanged(methodOfExecution: METHOD_EXECUTION) {
    this.methodOfExecutionValue = methodOfExecution;
    if (this.methodOfExecutionValue === METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
      this.contractTypeValue = CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED;
      this.selectedRecPrograms = [];
      this.generateApplicableRecProgramDD();
    } else {
      this.contractTypeValue = null;
    }
    this.handleContractTypeValue();
    if (
      this.methodOfExecutionValue === METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED &&
      (this.productTypeValue === PRODUCT_TYPE.RECS ||
        this.productTypeValue === PRODUCT_TYPE.PHYSICAL_PPA)
    ) {
      this.productTypeValue = '';
    }
    this.$emit('updateMethodOfExecution', this.methodOfExecutionValue);
    this.updateCriteria();
  }

  public contractTypeChanged(contractType?: CONTRACT_TYPE) {
    this.contractTypeValue = contractType;
    this.handleContractTypeValue();
    this.updateCriteria();
  }

  private handleContractTypeValue() {
    this.newBuildChecked = [CONTRACT_TYPE.NEW_BUILD_VPPA, CONTRACT_TYPE.NEW_BUILD_PPA].includes(
      this.contractTypeValue as CONTRACT_TYPE,
    );
    this.postBuildChecked = [
      CONTRACT_TYPE.POST_BUILD_VPPA,
      CONTRACT_TYPE.POST_BUILD_PPA,
      CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
    ].includes(this.contractTypeValue as CONTRACT_TYPE);
    if (!this.newBuildChecked && !this.postBuildChecked) {
      this.contractTypeValue = null;
    }
  }

  private getProjectOperationalStatus(): string {
    switch (this.contractTypeValue) {
      case CONTRACT_TYPE.NEW_BUILD_VPPA:
        return PROJECT_OPERATIONAL_STATUS.NEW_BUILD;
      case CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED:
        return PROJECT_OPERATIONAL_STATUS.OPERATIONAL;
      case CONTRACT_TYPE.POST_BUILD_VPPA:
        return PROJECT_OPERATIONAL_STATUS.OPERATIONAL;
      default:
        return null;
    }
  }

  private getContractTypeByProductType(): string {
    switch (this.productTypeValue) {
      case PRODUCT_TYPE.RECS:
        return CONTRACT_TYPE.REC;
      case PRODUCT_TYPE.PHYSICAL_PPA:
        if (this.contractTypeValue === CONTRACT_TYPE.NEW_BUILD_VPPA) {
          return CONTRACT_TYPE.NEW_BUILD_PPA;
        } else {
          return CONTRACT_TYPE.POST_BUILD_PPA;
        }
      default:
        return this.contractTypeValue;
    }
  }

  private async loadRecProgramEnums(): Promise<void> {
    const response = await this.enumService.getEnumValues(CASCADE_ENUM.APPLICABLE_REC_PROGRAM);
    if (response.REPLY) {
      this.applicableRECProgramTypes = response.REPLY;
    }
  }

  private resetISOChecklist() {
    const isoSet = new Set<string>();
    this._iso = [];

    this._hubZoneMatrix.forEach((item) => {
      if (!isoSet.has(item.ISO)) {
        const value: IMultiSelectOption = {
          CHECKED: null,
          ITEM_ID: item.ISO,
          ITEM_DESC: item.ISO,
          DISABLED: false,
          CATEGORY: '',
        };
        this._iso.push(value);
        isoSet.add(item.ISO);
      }
    });
  }

  private resetRecChecklist() {
    this._applicableRecProgramList = [
      ...new Set(
        this.applicableRECProgramTypes.map((item) => {
          const value: IMultiSelectOption = {
            CHECKED: false,
            ITEM_ID: item.ENUM_VALUE,
            ITEM_DESC: item.READABLE_VALUE,
            DISABLED: false,
            CATEGORY: '',
          };
          return value;
        }),
      ),
    ];
  }

  private resetSettlementPointChecklist() {
    this._settlementPointList = [
      ...new Set(
        this._settlementPoint.map((item) => {
          const value: IMultiSelectOption = {
            CHECKED: false,
            ITEM_ID: item,
            ITEM_DESC: item,
            DISABLED: false,
            CATEGORY: '',
          };
          return value;
        }),
      ),
    ];
  }
}
